import { ChevronUpIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  ChevronDownIcon,
  Flex,
  Grid,
  Separator,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSliderInput } from 'components/common/form/slider';
import { CommonTooltip } from 'components/common/tooltip';
import { RemoteCalibrationStream } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/remote-calibration-stream';
import { RemoteVisionCalibrationStageHeader } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/stage-header';
import useVisionRemoteStore, {
  DEFAULT_CAMERA_CONFIG,
} from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/store/useVisionRemoteStore';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { format } from 'date-fns-tz';
import { VisionRemoteStage } from 'enums/calibration';
import { t } from 'i18next';
import { feetFloatToString, IN_TO_MM } from 'lib_ts/classes/math.utilities';
import { LOCAL_VERBOSE_DATETIME_PRECISE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { VisionCalibrationCameraType } from 'lib_ts/enums/vision.enums';
import {
  IVisionCameraConfiguration,
  IVisionRemoteCalibrationRequest,
} from 'lib_ts/interfaces/machine-msg/vision/i-remote-calibration';
import { debounce, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';

type CalibrationCameraType =
  | VisionCalibrationCameraType.leader
  | VisionCalibrationCameraType.follower;

export type SendRemoteCalRequest = (
  payload: IVisionRemoteCalibrationRequest
) => Promise<void>;

interface IProps {
  cameraType: CalibrationCameraType;
  sendRemoteCalRequest: SendRemoteCalRequest;
}

interface IVisionSliderConfig {
  units: string;
  min: number;
  max: number;
  step: number;
  decimals: number;
  description: string;
}

//* TODO: Should we only send after use has released the slider?
const DEBOUNCE_MS = 1500;

const ANGLE_UNITS = 'deg';
const ANGLE_LIMIT_DEG = 8;
const ANGLE_STEP = 0.01;
const ANGLE_DECIMALS = 2;

const ANGLE_SLIDER_CONFIG: Record<string, IVisionSliderConfig> = {
  roll_deg: {
    units: ANGLE_UNITS,
    min: -ANGLE_LIMIT_DEG,
    max: ANGLE_LIMIT_DEG,
    step: ANGLE_STEP,
    decimals: ANGLE_DECIMALS,
    description: 'Rotation around the front-to-back axis of the camera.',
  },
  pitch_deg: {
    units: ANGLE_UNITS,
    min: -ANGLE_LIMIT_DEG,
    max: ANGLE_LIMIT_DEG,
    step: ANGLE_STEP,
    decimals: ANGLE_DECIMALS,
    description: 'Rotation around the side-to-side axis of the camera.',
  },
  yaw_deg: {
    units: ANGLE_UNITS,
    min: -ANGLE_LIMIT_DEG,
    max: ANGLE_LIMIT_DEG,
    step: ANGLE_STEP,
    decimals: ANGLE_DECIMALS,
    description: 'Rotation around the vertical axis of the camera.',
  },
};

const FOLLOWER_POSITION_LIMIT_MM = 250; // +/- 250 mm
const FOLLOWER_POSITION_UNITS = 'mm';

const POSITION_STEP = 1;
const POSITION_DECIMALS = 0;

const Y_POSITION_DESCRIPTION =
  'Moves the camera forward or backward along the field direction.';
const X_POSITION_DESCRIPTION =
  'Moves the camera left or right relative to the plate.';
const Z_POSITION_DESCRIPTION =
  'Moves the camera up or down relative to the plate.';

const FOLLOWER_POSITION_CONFIG: Omit<IVisionSliderConfig, 'description'> = {
  units: FOLLOWER_POSITION_UNITS,
  min: -FOLLOWER_POSITION_LIMIT_MM,
  max: FOLLOWER_POSITION_LIMIT_MM,
  step: POSITION_STEP,
  decimals: POSITION_DECIMALS,
};

const LEADER_POSITION_UNITS = 'in';
const LEADER_Y_LIMIT_IN = 240; // +/- 20 ft
const LEADER_XZ_LIMIT_IN = 120; // +/- 10 ft

const LEADER_Y_POSITION_CONFIG: IVisionSliderConfig = {
  units: LEADER_POSITION_UNITS,
  min: -LEADER_Y_LIMIT_IN,
  max: LEADER_Y_LIMIT_IN,
  step: POSITION_STEP,
  decimals: POSITION_DECIMALS,
  description: Y_POSITION_DESCRIPTION,
};

const LEADER_XZ_POSITION_CONFIG: Omit<IVisionSliderConfig, 'description'> = {
  units: LEADER_POSITION_UNITS,
  min: -LEADER_XZ_LIMIT_IN,
  max: LEADER_XZ_LIMIT_IN,
  step: POSITION_STEP,
  decimals: POSITION_DECIMALS,
};

//* All values get sent to AM in mm and deg, but FW would prefer if Leader positions are shown in inches
const CAMERA_SLIDER_CONFIGS: Record<
  CalibrationCameraType,
  Record<string, IVisionSliderConfig>
> = {
  [VisionCalibrationCameraType.leader]: {
    y_mm: LEADER_Y_POSITION_CONFIG,
    x_mm: {
      ...LEADER_XZ_POSITION_CONFIG,
      description: X_POSITION_DESCRIPTION,
    },
    z_mm: {
      ...LEADER_XZ_POSITION_CONFIG,
      description: Z_POSITION_DESCRIPTION,
    },
    ...ANGLE_SLIDER_CONFIG,
  },
  [VisionCalibrationCameraType.follower]: {
    y_mm: {
      ...FOLLOWER_POSITION_CONFIG,
      description: Y_POSITION_DESCRIPTION,
    },
    x_mm: {
      ...FOLLOWER_POSITION_CONFIG,
      description: X_POSITION_DESCRIPTION,
    },
    z_mm: {
      ...FOLLOWER_POSITION_CONFIG,
      description: Z_POSITION_DESCRIPTION,
    },
    ...ANGLE_SLIDER_CONFIG,
  },
};

// flexbox will shrink the separator down to 0px unless its wrapped
const Divider = () => (
  <Box>
    <Separator size="4" />
  </Box>
);

const SliderTooltip = ({ description }: { description: string }) => (
  <CommonTooltip
    trigger={<InfoCircledIcon color={RADIX.COLOR.SECONDARY} />}
    text_md={description}
  />
);

export const RemoteVisionCalibrationCameraCalibration = ({
  cameraType,
  sendRemoteCalRequest,
}: IProps) => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const lastResponse = useVisionRemoteStore((s) => s.lastResponse);
  const loading = useVisionRemoteStore((s) => s.loading);
  const leaderSliders = useVisionRemoteStore((s) => s.leaderSliders);
  const followerSliders = useVisionRemoteStore((s) => s.followerSliders);

  const [showAdvanced, setShowAdvanced] = useState(false);

  // we allow users to view both Leader and Follower streams, regardless of which camera they are calibrating
  const [streamSource, setStreamSource] =
    useState<VisionCalibrationCameraType>(cameraType);

  // this component will stay mounted between Leader and Follower stages, so we need to update the default stream sources
  useEffect(() => {
    setStreamSource(cameraType);
  }, [cameraType]);

  const recalculate = useMemo(
    () =>
      debounce(
        async (payload: IVisionRemoteCalibrationRequest): Promise<void> => {
          useVisionRemoteStore.getState().setLoading(true); // we'll set loading until we get a M2U_VisionRemoteCalibrationResponse back
          await sendRemoteCalRequest(payload);
        },
        DEBOUNCE_MS
      ),
    [sendRemoteCalRequest]
  );

  const sliders = useMemo(
    () =>
      cameraType === VisionCalibrationCameraType.leader
        ? leaderSliders
        : followerSliders,
    [cameraType, leaderSliders, followerSliders]
  );

  const currentValues = useMemo(
    () =>
      cameraType === VisionCalibrationCameraType.leader
        ? lastResponse?.leader
        : lastResponse?.follower,
    [cameraType, lastResponse]
  );

  const setSliders = useCallback(
    (config: IVisionCameraConfiguration) => {
      if (cameraType === VisionCalibrationCameraType.leader) {
        return useVisionRemoteStore.getState().setLeaderSliders(config);
      }

      return useVisionRemoteStore.getState().setFollowerSliders(config);
    },
    [cameraType]
  );

  useEffect(() => {
    recalculate({
      step: 'recalculate',
      leader: leaderSliders,
      follower: followerSliders,
    });
  }, [leaderSliders, followerSliders]);

  const onCancel = async () => {
    useVisionRemoteStore.getState().setLoading(true);

    await sendRemoteCalRequest({
      step: 'recalculate',
      leader: DEFAULT_CAMERA_CONFIG,
      follower: DEFAULT_CAMERA_CONFIG,
    });

    useVisionRemoteStore.getState().setStage(VisionRemoteStage.Overview);
    useVisionRemoteStore.getState().setLeaderSliders(DEFAULT_CAMERA_CONFIG);
    useVisionRemoteStore.getState().setFollowerSliders(DEFAULT_CAMERA_CONFIG);
  };

  // using this instead of MachineHelper.getDistanceOptionValue in case vision provides a value thats beyond our typical min/max
  const visionPlateDistance = feetFloatToString(
    lastResponse?.plate_distance_ft
  );

  if (!machine || !lastResponse) {
    return null;
  }

  const SLIDER_CONFIGS = CAMERA_SLIDER_CONFIGS[cameraType];

  return (
    <ErrorBoundary identifier="RemoteVisionCalibrationCameraCalibration">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.WRAPPER}
      >
        <RemoteVisionCalibrationStageHeader />
        <Grid
          columns="2"
          gap={RADIX.FLEX.GAP.MD}
          overflow="hidden"
          height="100%"
        >
          <Flex
            gap={RADIX.FLEX.GAP.SM}
            direction="column"
            overflow="auto"
            height="100%"
          >
            <RemoteCalibrationStream
              cameraType={streamSource}
              onChangeCameraType={setStreamSource}
            />
            <Box>
              <CommonCallout
                size="1"
                flexAlign="start"
                icon={
                  <Box mt="4px">
                    {loading ? <Spinner /> : <InfoCircledIcon />}
                  </Box>
                }
                color={RADIX.COLOR.ACCENT}
                content={
                  <Flex direction="column">
                    {visionPlateDistance && (
                      <Text weight="bold" size="1">
                        Vision Plate Distance: {visionPlateDistance}
                      </Text>
                    )}
                    <Text size="1">
                      {t('common.last-updated-on-x', {
                        x: format(
                          new Date(lastResponse.send_time * 1000),
                          LOCAL_VERBOSE_DATETIME_PRECISE_FORMAT
                        ),
                      })}
                    </Text>
                  </Flex>
                }
              />
            </Box>
          </Flex>
          <Flex
            gap={RADIX.FLEX.GAP.MD}
            direction="column"
            className={RADIX.VFLEX.WRAPPER}
          >
            <Card className="no-padding no-background">
              <Flex direction="column" p="3" className={RADIX.VFLEX.SCROLLABLE}>
                {/** Clear slider local state via key when camera type changes */}
                <Flex
                  direction="column"
                  gap={RADIX.FLEX.GAP.MD}
                  key={cameraType}
                >
                  <Box>
                    <Text
                      size="3"
                      style={{
                        color: RADIX.COLOR.SAFE_TEXT,
                      }}
                      weight="medium"
                    >
                      {`${startCase(cameraType)} Camera Controls`}
                    </Text>
                  </Box>
                  <CommonSliderInput
                    id="vision-remote-calibration-roll"
                    label="Roll"
                    labelIcon={
                      <SliderTooltip
                        description={SLIDER_CONFIGS.roll_deg.description}
                      />
                    }
                    value={sliders.roll_deg}
                    customCurrent={currentValues?.roll_deg}
                    onChange={(value) =>
                      setSliders({ ...sliders, roll_deg: value })
                    }
                    min={SLIDER_CONFIGS.roll_deg.min}
                    max={SLIDER_CONFIGS.roll_deg.max}
                    step={SLIDER_CONFIGS.roll_deg.step}
                    units={SLIDER_CONFIGS.roll_deg.units}
                    decimals={SLIDER_CONFIGS.roll_deg.decimals}
                    disabled={loading}
                    withInput
                  />
                  <Separator size="4" />
                  <CommonSliderInput
                    id="vision-remote-calibration-pitch"
                    label="Pitch"
                    labelIcon={
                      <SliderTooltip
                        description={SLIDER_CONFIGS.pitch_deg.description}
                      />
                    }
                    value={sliders.pitch_deg}
                    customCurrent={currentValues?.pitch_deg}
                    onChange={(value) =>
                      setSliders({ ...sliders, pitch_deg: value })
                    }
                    min={SLIDER_CONFIGS.pitch_deg.min}
                    max={SLIDER_CONFIGS.pitch_deg.max}
                    step={SLIDER_CONFIGS.pitch_deg.step}
                    units={SLIDER_CONFIGS.pitch_deg.units}
                    decimals={SLIDER_CONFIGS.pitch_deg.decimals}
                    disabled={loading}
                    withInput
                  />
                  <Divider />
                  <CommonSliderInput
                    id="vision-remote-calibration-yaw"
                    label="Yaw"
                    labelIcon={
                      <SliderTooltip
                        description={SLIDER_CONFIGS.yaw_deg.description}
                      />
                    }
                    value={sliders.yaw_deg}
                    customCurrent={currentValues?.yaw_deg}
                    onChange={(value) =>
                      setSliders({ ...sliders, yaw_deg: value })
                    }
                    min={SLIDER_CONFIGS.yaw_deg.min}
                    max={SLIDER_CONFIGS.yaw_deg.max}
                    step={SLIDER_CONFIGS.yaw_deg.step}
                    units={SLIDER_CONFIGS.yaw_deg.units}
                    decimals={SLIDER_CONFIGS.yaw_deg.decimals}
                    disabled={loading}
                    withInput
                  />
                  {
                    // follower Y Translation is an advanced control
                    cameraType === VisionCalibrationCameraType.leader && (
                      <CommonSliderInput
                        id="vision-remote-calibration-y"
                        label="Y Translation (Plate Distance)"
                        labelIcon={
                          <SliderTooltip
                            description={SLIDER_CONFIGS.y_mm.description}
                          />
                        }
                        value={sliders.y_mm / IN_TO_MM}
                        customCurrent={(currentValues?.y_mm ?? 0) / IN_TO_MM}
                        onChange={(value) =>
                          setSliders({ ...sliders, y_mm: value * IN_TO_MM })
                        }
                        min={SLIDER_CONFIGS.y_mm.min}
                        max={SLIDER_CONFIGS.y_mm.max}
                        step={SLIDER_CONFIGS.y_mm.step}
                        units={SLIDER_CONFIGS.y_mm.units}
                        decimals={SLIDER_CONFIGS.y_mm.decimals}
                        disabled={loading}
                        withInput
                      />
                    )
                  }
                  <Divider />
                  <Box>
                    <Button
                      color={RADIX.COLOR.ACCENT}
                      variant="soft"
                      onClick={() => setShowAdvanced((prev) => !prev)}
                    >
                      Advanced Controls{' '}
                      {showAdvanced ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Button>
                  </Box>
                </Flex>
                <AnimateHeight
                  duration={200}
                  height={showAdvanced ? 'auto' : 0}
                  easing="ease-in-out"
                >
                  <Flex
                    direction="column"
                    gap={RADIX.FLEX.GAP.MD}
                    mt={RADIX.FLEX.GAP.MD}
                  >
                    <CommonCallout
                      content={
                        <Text color={RADIX.COLOR.SECONDARY}>
                          This section is reserved for engineering. Please use
                          carefully.
                        </Text>
                      }
                    />
                    {cameraType === VisionCalibrationCameraType.follower && (
                      <CommonSliderInput
                        id="vision-remote-calibration-y"
                        label="Y Translation (Plate Distance)"
                        labelIcon={
                          <SliderTooltip
                            description={SLIDER_CONFIGS.y_mm.description}
                          />
                        }
                        value={sliders.y_mm / IN_TO_MM}
                        customCurrent={(currentValues?.y_mm ?? 0) / IN_TO_MM}
                        onChange={(value) =>
                          setSliders({ ...sliders, y_mm: value * IN_TO_MM })
                        }
                        min={SLIDER_CONFIGS.y_mm.min}
                        max={SLIDER_CONFIGS.y_mm.max}
                        step={SLIDER_CONFIGS.y_mm.step}
                        units={SLIDER_CONFIGS.y_mm.units}
                        decimals={SLIDER_CONFIGS.y_mm.decimals}
                        disabled={loading}
                        withInput
                      />
                    )}
                    <CommonSliderInput
                      id="vision-remote-calibration-x"
                      label="X Translation"
                      labelIcon={
                        <SliderTooltip
                          description={SLIDER_CONFIGS.x_mm.description}
                        />
                      }
                      value={sliders.x_mm / IN_TO_MM}
                      customCurrent={(currentValues?.x_mm ?? 0) / IN_TO_MM}
                      onChange={(value) =>
                        setSliders({ ...sliders, x_mm: value * IN_TO_MM })
                      }
                      min={SLIDER_CONFIGS.x_mm.min}
                      max={SLIDER_CONFIGS.x_mm.max}
                      step={SLIDER_CONFIGS.x_mm.step}
                      units={SLIDER_CONFIGS.x_mm.units}
                      decimals={SLIDER_CONFIGS.x_mm.decimals}
                      disabled={loading}
                      withInput
                    />
                    <Divider />
                    <CommonSliderInput
                      id="vision-remote-calibration-z"
                      label="Z Translation"
                      labelIcon={
                        <SliderTooltip
                          description={SLIDER_CONFIGS.z_mm.description}
                        />
                      }
                      value={sliders.z_mm / IN_TO_MM}
                      customCurrent={(currentValues?.z_mm ?? 0) / IN_TO_MM}
                      onChange={(value) =>
                        setSliders({ ...sliders, z_mm: value * IN_TO_MM })
                      }
                      min={SLIDER_CONFIGS.z_mm.min}
                      max={SLIDER_CONFIGS.z_mm.max}
                      step={SLIDER_CONFIGS.z_mm.step}
                      units={SLIDER_CONFIGS.z_mm.units}
                      decimals={SLIDER_CONFIGS.z_mm.decimals}
                      disabled={loading}
                      withInput
                    />
                  </Flex>
                </AnimateHeight>
              </Flex>
            </Card>
            <Flex ml="auto" gap={RADIX.FLEX.GAP.SM} mt="auto">
              <DialogButton
                label="Cancel"
                color={RADIX.COLOR.SECONDARY}
                loading={loading}
                onClick={onCancel}
              />
              <DialogButton
                label="Back"
                color={RADIX.COLOR.ACCENT}
                loading={loading}
                onClick={useVisionRemoteStore.getState().prevStage}
              />
              <DialogButton
                label="Continue"
                color={RADIX.COLOR.ACCENT}
                loading={loading}
                onClick={useVisionRemoteStore.getState().nextStage}
                variant="solid"
              />
            </Flex>
          </Flex>
        </Grid>
      </Flex>
    </ErrorBoundary>
  );
};
